import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, Crown, Medal, Star, TrendingUp, Gamepad2,
  ChevronRight, Search, Filter, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('elo');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLeaderboard();
  }, [sortBy]);

  const fetchLeaderboard = async () => {
    try {
      let endpoint = sortBy === 'elo' 
        ? `${API}/matchmaking/leaderboard-elo?limit=50`
        : `${API}/leaderboard?sort_by=${sortBy}&limit=50`;
      const response = await axios.get(endpoint);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return { icon: Crown, color: '#FFD700', bg: 'bg-[#FFD700]/20' };
    if (rank === 2) return { icon: Medal, color: '#C0C0C0', bg: 'bg-[#C0C0C0]/20' };
    if (rank === 3) return { icon: Medal, color: '#CD7F32', bg: 'bg-[#CD7F32]/20' };
    return { icon: Star, color: '#7000FF', bg: 'bg-[#7000FF]/20' };
  };

  const getEloTier = (elo) => {
    if (elo >= 2000) return { label: 'GM', color: '#FFD700' };
    if (elo >= 1800) return { label: 'M', color: '#7000FF' };
    if (elo >= 1600) return { label: 'D', color: '#00F0FF' };
    if (elo >= 1400) return { label: 'P', color: '#00FF94' };
    if (elo >= 1200) return { label: 'G', color: '#CCFF00' };
    if (elo >= 1000) return { label: 'S', color: '#C0C0C0' };
    return { label: 'B', color: '#CD7F32' };
  };

  const filteredLeaderboard = leaderboard.filter(player =>
    player.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="leaderboard-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 mb-4">
            <Trophy className="w-4 h-4 text-[#FFD700]" />
            <span className="text-sm text-[#FFD700] font-medium">Global Rankings</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">Leaderboard</h1>
          <p className="text-white/50">Top competitors on MatchStake</p>
        </div>

        {/* Filters */}
        <div className="glass-card rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-dark"
                data-testid="leaderboard-search"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-48 bg-white/5 border-white/10" data-testid="sort-select">
                <Filter className="w-4 h-4 mr-2 text-white/50" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0B] border-white/10">
                <SelectItem value="elo">ELO Rating</SelectItem>
                <SelectItem value="wins">Most Wins</SelectItem>
                <SelectItem value="earnings">Highest Earnings</SelectItem>
                <SelectItem value="win_rate">Best Win Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ELO Legend */}
        {sortBy === 'elo' && (
          <div className="glass-card rounded-xl p-4 mb-6">
            <p className="text-white/50 text-sm mb-3">ELO Tiers</p>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Grandmaster', abbr: 'GM', color: '#FFD700', min: '2000+' },
                { label: 'Master', abbr: 'M', color: '#7000FF', min: '1800' },
                { label: 'Diamond', abbr: 'D', color: '#00F0FF', min: '1600' },
                { label: 'Platinum', abbr: 'P', color: '#00FF94', min: '1400' },
                { label: 'Gold', abbr: 'G', color: '#CCFF00', min: '1200' },
                { label: 'Silver', abbr: 'S', color: '#C0C0C0', min: '1000' },
                { label: 'Bronze', abbr: 'B', color: '#CD7F32', min: '<1000' },
              ].map(tier => (
                <div 
                  key={tier.abbr}
                  className="flex items-center gap-2 px-3 py-1 rounded-full"
                  style={{ backgroundColor: `${tier.color}15` }}
                >
                  <span className="font-bold text-sm" style={{ color: tier.color }}>{tier.abbr}</span>
                  <span className="text-white/50 text-xs">{tier.min}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top 3 Podium */}
        {!loading && filteredLeaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* Second Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-xl p-6 text-center mt-8"
            >
              <div className="w-16 h-16 mx-auto mb-3 relative">
                <Avatar className="w-16 h-16 border-4 border-[#C0C0C0]">
                  <AvatarImage src={filteredLeaderboard[1]?.picture} />
                  <AvatarFallback className="bg-[#C0C0C0] text-black text-xl">
                    {filteredLeaderboard[1]?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#C0C0C0] flex items-center justify-center">
                  <span className="text-black font-bold text-sm">2</span>
                </div>
              </div>
              <p className="font-semibold truncate">{filteredLeaderboard[1]?.name}</p>
              {sortBy === 'elo' ? (
                <p className="font-mono text-[#C0C0C0] text-lg">
                  {filteredLeaderboard[1]?.elo_rating || 1200}
                  <Zap className="w-4 h-4 inline ml-1" />
                </p>
              ) : (
                <p className="font-mono text-[#C0C0C0] text-lg">{filteredLeaderboard[1]?.wins} W</p>
              )}
            </motion.div>

            {/* First Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-xl p-6 text-center border-2 border-[#FFD700]/30 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-[#FFD700]/10 to-transparent" />
              <Crown className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
              <div className="w-20 h-20 mx-auto mb-3 relative">
                <Avatar className="w-20 h-20 border-4 border-[#FFD700]">
                  <AvatarImage src={filteredLeaderboard[0]?.picture} />
                  <AvatarFallback className="bg-[#FFD700] text-black text-2xl">
                    {filteredLeaderboard[0]?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center">
                  <span className="text-black font-bold text-sm">1</span>
                </div>
              </div>
              <p className="font-semibold text-lg truncate relative z-10">{filteredLeaderboard[0]?.name}</p>
              {sortBy === 'elo' ? (
                <p className="font-mono text-[#FFD700] text-xl relative z-10">
                  {filteredLeaderboard[0]?.elo_rating || 1200}
                  <Zap className="w-4 h-4 inline ml-1" />
                </p>
              ) : (
                <p className="font-mono text-[#FFD700] text-xl relative z-10">{filteredLeaderboard[0]?.wins} W</p>
              )}
              <p className="text-xs text-white/50 mt-1">${filteredLeaderboard[0]?.total_earnings?.toFixed(0)} earned</p>
            </motion.div>

            {/* Third Place */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-xl p-6 text-center mt-12"
            >
              <div className="w-14 h-14 mx-auto mb-3 relative">
                <Avatar className="w-14 h-14 border-4 border-[#CD7F32]">
                  <AvatarImage src={filteredLeaderboard[2]?.picture} />
                  <AvatarFallback className="bg-[#CD7F32] text-black text-lg">
                    {filteredLeaderboard[2]?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-[#CD7F32] flex items-center justify-center">
                  <span className="text-black font-bold text-xs">3</span>
                </div>
              </div>
              <p className="font-semibold text-sm truncate">{filteredLeaderboard[2]?.name}</p>
              {sortBy === 'elo' ? (
                <p className="font-mono text-[#CD7F32]">
                  {filteredLeaderboard[2]?.elo_rating || 1200}
                  <Zap className="w-3 h-3 inline ml-1" />
                </p>
              ) : (
                <p className="font-mono text-[#CD7F32]">{filteredLeaderboard[2]?.wins} W</p>
              )}
            </motion.div>
          </div>
        )}

        {/* Full Leaderboard */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="font-heading font-semibold">All Rankings</h2>
          </div>
          
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredLeaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No players found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredLeaderboard.map((player, index) => {
                const rankConfig = getRankIcon(index + 1);
                const winRate = player.wins + player.losses > 0
                  ? ((player.wins / (player.wins + player.losses)) * 100).toFixed(1)
                  : 0;
                const eloTier = getEloTier(player.elo_rating || 1200);

                return (
                  <motion.div
                    key={player.user_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => navigate(`/player/${player.user_id}`)}
                    className="flex items-center justify-between p-4 hover:bg-white/5 cursor-pointer transition-colors"
                    data-testid={`leaderboard-rank-${index + 1}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${rankConfig.bg}`}
                      >
                        {index < 3 ? (
                          <rankConfig.icon className="w-5 h-5" style={{ color: rankConfig.color }} />
                        ) : (
                          <span className="font-mono font-bold text-white/50">{index + 1}</span>
                        )}
                      </div>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={player.picture} />
                        <AvatarFallback className="bg-[#7000FF] text-white">
                          {player.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{player.name}</p>
                          {sortBy === 'elo' && (
                            <span 
                              className="text-xs px-2 py-0.5 rounded font-bold"
                              style={{ backgroundColor: `${eloTier.color}20`, color: eloTier.color }}
                            >
                              {eloTier.label}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-white/40">
                          <span className="text-[#00FF94]">{player.wins}W</span>
                          <span>/</span>
                          <span className="text-[#FF2E2E]">{player.losses}L</span>
                          <span className="text-white/20">•</span>
                          <span>{winRate}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {sortBy === 'elo' ? (
                          <>
                            <p className="font-mono text-lg" style={{ color: eloTier.color }}>
                              {player.elo_rating || 1200}
                            </p>
                            <p className="text-xs text-white/40">ELO</p>
                          </>
                        ) : (
                          <>
                            <p className="font-mono text-[#CCFF00]">
                              ${player.total_earnings?.toFixed(0) || 0}
                            </p>
                            <p className="text-xs text-white/40">earned</p>
                          </>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-white/30" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Leaderboard;
